import React, { useState } from 'react';
import { ClipboardIcon, Folder01Icon, InternetIcon, SourceCodeIcon, Tick02Icon } from 'hugeicons-react';
import GoogleDriveIconSvg from '../../../assets/svg/Google_Drive_icon';
import { useAuth } from '../../../contexts/AuthContext';
import { chatConfig } from '../../../config/chatConfig';
import { ACCEPTED_FILE_TYPES } from '../../../config/AcceptedFileTypes';
import { HoverToolTip } from '../Items/HoverToolTip';
import { useAttachmentContext } from '../../../contexts/AttachmentContext';
import '../../../Styles/Layout/Input-Area/AttachmentModal.css';
import { useChatContext } from '../../../contexts/AppDataContext';
import { useWindow } from '../../../contexts/WindowContext';
import { retrieveGoogleDriveFiles } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { AttachmentDropIcons } from '../../../assets/svg/AttachmentDropIcons';
import { LoginButton } from '../../Auth/Buttons/LoginButton';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../../ui/dropdown-menu';

export const PRE_DRIVE_REDIRECT_CHAT_KEY = 'pre-drive-redirect-chat';

interface AttachButtonProps {
  isChat: boolean;
}

const MENU_ITEM_STYLE: React.CSSProperties = {
  padding: 'var(--spacing-sm)',
  gap: 'var(--spacing-sm)',
  borderRadius: 'var(--radius-lg)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-primary)',
  fontSize: 'var(--font-size-xs)',
};

const MENU_ITEM_CLASS =
  'hover:!bg-[var(--background-hover)] focus:!bg-[var(--background-hover)] data-[state=open]:!bg-[var(--background-hover)] [&_svg]:!size-auto cursor-pointer';

const HIDDEN_FILE_INPUT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  opacity: 0,
  pointerEvents: 'none',
};

const AttachButton: React.FC<AttachButtonProps> = ({ isChat }) => {
  const [open, setOpen] = useState(false);
  const {
    processFiles,
    fileInputRef,
    handleCreateDocumentClick,
    handleGoogleDriveModalOpen,
    googleDriveFiles,
    setGoogleDriveFetchState,
  } = useAttachmentContext();
  const { windowWidth } = useWindow();
  const auth = useAuth();
  const { currentChatId, toggleWebSearch, useWeb } = useChatContext();
  const toast = useToast();

  const acceptString = Object.entries(ACCEPTED_FILE_TYPES)
    .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
    .join(',');
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const openFilePicker = () => {
    setOpen(false);
    fileInputRef.current?.click();
  };

  const handleGoogleDriveClick = async () => {
    if (!auth.user?.is_gdrive_enabled) {
      localStorage.setItem(PRE_DRIVE_REDIRECT_CHAT_KEY, currentChatId || '');
      auth.connectGoogleDrive();
      return;
    }

    if (googleDriveFiles.length > 0) {
      handleGoogleDriveModalOpen(googleDriveFiles, false);
      return;
    }

    handleGoogleDriveModalOpen([], true);
    setOpen(false);
    try {
      const response = await retrieveGoogleDriveFiles();
      if (response.success) {
        setGoogleDriveFetchState(response.files, false, null);
      } else {
        const message = response.error && response.message ? response.message : 'Error retrieving Google Drive files';
        toast(message, 'error');
        setGoogleDriveFetchState([], false, message);
      }
    } catch (error) {
      console.error('Error retrieving Google Drive files:', error);
      toast('An unexpected error occurred while fetching Google Drive files.', 'error');
      setGoogleDriveFetchState([], false, 'An unexpected error occurred while fetching Google Drive files.');
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`attach-button ${open ? 'active' : ''}`}
          aria-label="Attach files"
          title="Attach files"
        >
          <Folder01Icon size={18} />
          <HoverToolTip
            text={`Upload up to ${chatConfig.limits.files.MAX_FILES_PER_MESSAGE} files`}
            position="bottom"
          />
        </button>
      </DropdownMenuTrigger>

      <input
        type="file"
        accept={acceptString}
        multiple
        onChange={(e) => processFiles(e)}
        ref={fileInputRef}
        aria-hidden="true"
        tabIndex={-1}
        style={HIDDEN_FILE_INPUT_STYLE}
      />

      <DropdownMenuContent
        side={isChat ? 'top' : 'bottom'}
        align="start"
        sideOffset={4}
        className="w-[250px] bg-[var(--background-light)] border-[var(--border-secondary)] rounded-[var(--radius-xl)] shadow-none gap-0"
      >
        {!auth.user && !auth.isAuthenticated && (
          <div className="attachment-modal-guest-display">
            <div className="attachment-modal-guest-display-body">
              <div>
                <span className="attachment-modal-guest-display-description">
                  You need to be signed in to upload files.
                </span>
              </div>
              <div>
                <LoginButton />
              </div>
            </div>
          </div>
        )}

        {windowWidth >= 768 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={!auth.user}
              className={`${MENU_ITEM_CLASS} [&>svg:last-child]:!size-[14px]`}
              style={MENU_ITEM_STYLE}
            >
              <span>Create Document</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              sideOffset={4}
              className="bg-[var(--background-light)] border-[var(--border-secondary)] rounded-[var(--radius-xl)] shadow-none flex flex-col gap-[var(--spacing-xs)]"
              style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
            >
              <DropdownMenuItem
                disabled={!auth.user}
                onSelect={() => handleCreateDocumentClick('code')}
                className={`${MENU_ITEM_CLASS} attachment-side-modal-item code`}
                style={{
                  padding: 'var(--spacing-sm)',
                  gap: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <SourceCodeIcon size={16} />
                <span>Code</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!auth.user}
                onSelect={() => handleCreateDocumentClick('doc')}
                className={`${MENU_ITEM_CLASS} attachment-side-modal-item doc`}
                style={{
                  padding: 'var(--spacing-sm)',
                  gap: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-lg)',
                }}
              >
                <ClipboardIcon size={16} />
                <span>Document</span>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {windowWidth < 768 && (
          <DropdownMenuItem
            disabled={!auth.user}
            onSelect={() => handleCreateDocumentClick('doc')}
            className={MENU_ITEM_CLASS}
            style={MENU_ITEM_STYLE}
          >
            <ClipboardIcon size={16} />
            <span>Create Document</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          disabled={!auth.user}
          onSelect={() => openFilePicker()}
          className={MENU_ITEM_CLASS}
          style={MENU_ITEM_STYLE}
        >
          <AttachmentDropIcons size={18} />
          <span>Add Photos and Files</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!auth.user}
          onSelect={(e) => {
            e.preventDefault();
            toggleWebSearch();
          }}
          className={`${MENU_ITEM_CLASS} justify-between`}
          style={{
            ...MENU_ITEM_STYLE,
            ...(useWeb ? { color: 'var(--primary-color)' } : {}),
          }}
        >
          <div className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
            <InternetIcon size={16} />
            <span>Web Search</span>
          </div>
          {useWeb && (
            <Tick02Icon size={18} className="attachment-modal-action-item-button-tick" />
          )}
        </DropdownMenuItem>

        {chatConfig.limits.files.IS_DRIVE_ENABLED && (
          <DropdownMenuItem
            disabled={!auth.user}
            onSelect={handleGoogleDriveClick}
            className={`${MENU_ITEM_CLASS} justify-between`}
            style={MENU_ITEM_STYLE}
          >
            <span>
              {auth.user?.is_gdrive_enabled
                ? 'Upload from Google Drive'
                : 'Connect to Google Drive'}
            </span>
            <GoogleDriveIconSvg size={18} />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AttachButton;
